import { X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { cn } from '~shared/shadcn/lib/utils';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';
import { Input } from '~shared/shadcn/ui/input';

import {
  activeFilterChipLabel,
  filterOptionChoices,
  optionFilterLabel,
  selectedOptionValues,
  toggleOptionValue,
} from '../lib/class-list-option-filter';

export function ClassOptionFilter({
  value,
  onChange,
  options,
  allLabel,
  className,
  searchPlaceholder,
}: {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  allLabel: string;
  className?: string;
  searchPlaceholder?: string;
}) {
  const [search, setSearch] = useState('');
  const selected = new Set(selectedOptionValues(value));
  const visibleOptions = useMemo(
    () => filterOptionChoices(options, search),
    [options, search],
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant='outline'
          className={cn(
            selected.size > 0 &&
              'border-primary bg-primary/20 text-primary-foreground hover:bg-primary/30',
            className,
          )}
        >
          <span className='truncate'>
            {optionFilterLabel(value, options, allLabel)}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='start'
        className='max-h-72 w-64 overflow-y-auto'
        onCloseAutoFocus={() => {
          setSearch('');
        }}
      >
        {searchPlaceholder ? (
          <div
            className='sticky top-0 z-10 bg-popover p-2'
            onKeyDown={(event) => {
              event.stopPropagation();
            }}
          >
            <Input
              value={search}
              placeholder={searchPlaceholder}
              className='h-8'
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
          </div>
        ) : null}
        {visibleOptions.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={selected.has(option.value)}
            className='data-[state=checked]:bg-primary/20 data-[state=checked]:text-primary-foreground'
            onSelect={(event) => {
              event.preventDefault();
            }}
            onCheckedChange={() => {
              onChange(
                toggleOptionValue(
                  value,
                  option.value,
                  options.map((item) => item.value),
                ),
              );
            }}
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
        {visibleOptions.length === 0 ? (
          <p className='px-3 py-2 text-sm text-muted-foreground'>
            No options found
          </p>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ClassFilterChip({
  label,
  value,
  options,
  onClear,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onClear: () => void;
}) {
  const text = activeFilterChipLabel(value, options);
  if (!text) {
    return null;
  }

  return (
    <Badge variant='secondary' className='gap-1'>
      {label}: {text}
      <button
        type='button'
        onClick={onClear}
        className='ml-1 rounded-full hover:bg-secondary-foreground/20'
      >
        <X className='size-3' />
      </button>
    </Badge>
  );
}
